import type {
  LedgerReportCurrencyView,
  LedgerReportView,
  PaymentMethod,
  ReconciliationCheckName,
  ReportChargeTotalsView,
  ReportPaymentTotalsView,
  SupportedLocale,
} from '@stm/contracts';
import { translate, type TranslationKey } from '@stm/i18n';
import { paymentMethodKeys } from './billing';
import { csvTextCell } from './csv';

export interface ReportFilterChoices {
  /** Empty means today. */
  asOf: string;
  seasonId: string;
  teamSeasonId: string;
  method: PaymentMethod | '';
  accountId: string;
}

export const emptyReportFilters: ReportFilterChoices = {
  asOf: '',
  seasonId: '',
  teamSeasonId: '',
  method: '',
  accountId: '',
};

/** The query string of the chosen filters; an unchosen filter is left out. */
export function reportQueryString(choices: ReportFilterChoices): string {
  const parameters = new URLSearchParams();
  if (choices.asOf !== '') parameters.set('asOf', choices.asOf);
  if (choices.seasonId !== '') parameters.set('seasonId', choices.seasonId);
  if (choices.teamSeasonId !== '') parameters.set('teamSeasonId', choices.teamSeasonId);
  if (choices.method !== '') parameters.set('method', choices.method);
  if (choices.accountId !== '') parameters.set('accountId', choices.accountId);
  const text = parameters.toString();
  return text === '' ? '' : `?${text}`;
}

/** Team and payment method cannot be combined: a charge has no method and a payment has no team. */
export function filtersWithTeam(
  choices: ReportFilterChoices,
  teamSeasonId: string,
): ReportFilterChoices {
  return { ...choices, teamSeasonId, method: teamSeasonId === '' ? choices.method : '' };
}

export function filtersWithMethod(
  choices: ReportFilterChoices,
  method: PaymentMethod | '',
): ReportFilterChoices {
  return { ...choices, method, teamSeasonId: method === '' ? choices.teamSeasonId : '' };
}

/** A season change drops a team of another season. */
export function filtersWithSeason(
  choices: ReportFilterChoices,
  seasonId: string,
  teams: readonly { id: string; seasonId: string }[],
): ReportFilterChoices {
  const team = teams.find((candidate) => candidate.id === choices.teamSeasonId);
  const keepTeam = seasonId === '' || team?.seasonId === seasonId;
  return { ...choices, seasonId, teamSeasonId: keepTeam ? choices.teamSeasonId : '' };
}

export const reconciliationCheckKeys: Readonly<Record<ReconciliationCheckName, TranslationKey>> = {
  balance_matches_statements: 'billing.reports.check.balance',
  charges_settle_exactly: 'billing.reports.check.settle',
  allocations_match: 'billing.reports.check.allocations',
  payments_accounted: 'billing.reports.check.payments',
};

/** An amount in minor units as a plain decimal with a point, whatever the language (for CSV cells). */
export function decimalAmount(minorUnits: string): string {
  const amount = BigInt(minorUnits);
  const absolute = amount < 0n ? -amount : amount;
  const whole = (absolute / 100n).toString();
  const fraction = (absolute % 100n).toString().padStart(2, '0');
  return `${amount < 0n ? '-' : ''}${whole}.${fraction}`;
}

export interface ReportFilterNames {
  season: string | null;
  team: string | null;
  account: string | null;
}

function row(cells: readonly string[]): string {
  return cells.join(',');
}

function text(value: string): string {
  return csvTextCell(value);
}

function chargeRow(label: string, totals: ReportChargeTotalsView): string {
  return row([
    text(label),
    String(totals.count),
    decimalAmount(totals.chargedMinor),
    decimalAmount(totals.creditedMinor),
    decimalAmount(totals.allocatedMinor),
    decimalAmount(totals.outstandingMinor),
    decimalAmount(totals.overdueMinor),
    decimalAmount(totals.upcomingMinor),
  ]);
}

function paymentRow(label: string, totals: ReportPaymentTotalsView): string {
  return row([
    text(label),
    String(totals.count),
    decimalAmount(totals.receivedMinor),
    decimalAmount(totals.reversedMinor),
    decimalAmount(totals.allocatedMinor),
    decimalAmount(totals.refundedMinor),
    decimalAmount(totals.unallocatedCreditMinor),
  ]);
}

function currencyLines(
  block: LedgerReportCurrencyView,
  locale: SupportedLocale,
  asOf: string,
): string[] {
  const label = (key: TranslationKey): string => text(translate(locale, key));
  const lines = [
    row([
      label('billing.reports.currency'),
      text(block.currency),
      label('billing.reports.accounts'),
      String(block.accountCount),
    ]),
  ];
  if (block.charges !== null) {
    lines.push(
      row([label('billing.reports.byTeam')]),
      row([
        label('billing.reports.col.team'),
        label('billing.reports.col.count'),
        label('billing.reports.col.charged'),
        label('billing.reports.col.credited'),
        label('billing.reports.col.allocated'),
        label('billing.col.outstanding'),
        label('billing.position.overdue'),
        label('billing.position.upcoming'),
      ]),
    );
    for (const team of block.charges.byTeam) {
      lines.push(
        chargeRow(team.teamName ?? translate(locale, 'billing.reports.noTeam'), team.totals),
      );
    }
    lines.push(chargeRow(translate(locale, 'billing.reports.total'), block.charges.totals));
  }
  if (block.payments !== null) {
    lines.push(
      row([label('billing.reports.byMethod')]),
      row([
        label('billing.reports.col.method'),
        label('billing.reports.col.count'),
        label('billing.reports.col.received'),
        label('billing.reports.col.reversed'),
        label('billing.reports.col.allocated'),
        label('billing.reports.col.refunded'),
        label('billing.position.credit'),
      ]),
    );
    for (const method of block.payments.byMethod) {
      lines.push(paymentRow(translate(locale, paymentMethodKeys[method.method]), method.totals));
    }
    lines.push(paymentRow(translate(locale, 'billing.reports.total'), block.payments.totals));
  }
  lines.push(
    row([label('billing.reports.reconciliation.title'), text(asOf)]),
    row([
      label('billing.reports.reconciliation.check'),
      label('billing.reports.reconciliation.expected'),
      label('billing.reports.reconciliation.actual'),
      label('billing.reports.reconciliation.result'),
    ]),
  );
  for (const check of block.reconciliation) {
    lines.push(
      row([
        text(translate(locale, reconciliationCheckKeys[check.code])),
        decimalAmount(check.expectedMinor),
        decimalAmount(check.actualMinor),
        text(
          translate(
            locale,
            check.ok
              ? 'billing.reports.reconciliation.ok'
              : 'billing.reports.reconciliation.mismatch',
          ),
        ),
      ]),
    );
  }
  return lines;
}

/**
 * The report as CSV in the reader's language, with a byte-order mark so spreadsheets read the
 * accents. It opens with a manifest of what the figures cover (day, season, team, method, account and
 * when it was generated); every name goes through the formula-injection guard and amounts keep a
 * point as decimal separator. Blocks are per currency and never summed.
 */
export function ledgerReportCsv(
  report: LedgerReportView,
  locale: SupportedLocale,
  names: ReportFilterNames,
): string {
  const label = (key: TranslationKey): string => text(translate(locale, key));
  const everything = translate(locale, 'billing.reports.any');
  const lines = [
    row([label('billing.reports.title')]),
    row([label('billing.reports.asOf'), text(report.asOf)]),
    row([label('billing.reports.generatedAt'), text(report.generatedAt)]),
    row([label('billing.reports.season'), text(names.season ?? everything)]),
    row([label('billing.reports.team'), text(names.team ?? everything)]),
    row([
      label('billing.reports.method'),
      text(
        report.filters.method === null
          ? everything
          : translate(locale, paymentMethodKeys[report.filters.method]),
      ),
    ]),
    row([label('billing.reports.account'), text(names.account ?? everything)]),
  ];
  for (const block of report.currencies) {
    lines.push('', ...currencyLines(block, locale, report.asOf));
  }
  return `\uFEFF${lines.join('\r\n')}\r\n`;
}
